import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { ProCard, ProStat, ProTag, ProAction, ProEmpty, timeAgo } from "./ProCard";
import {
    UserSearch, Megaphone, Rss, ScanSearch, BarChart3, FileText, Home, Users, Eye, TrendingUp,
    Bell, BellOff, History, MessageSquare, Handshake, Plus, Download, RefreshCw, MapPin, ChevronRight, Map, X, Target,
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";

const safeGet = (url, params) => api.get(url, params ? { params } : undefined).then((r) => r.data).catch(() => null);

export default function AgencyProGrid({ user, onToggleEmail, onOpenSection }) {
    const [stats, setStats] = useState(null);
    const [reports, setReports] = useState([]);
    const [properties, setProperties] = useState([]);
    const [privates, setPrivates] = useState(null);
    const [social, setSocial] = useState(null);

    const loadSocial = () => safeGet("/crm/social-radar", { date_filter: "24h" }).then(setSocial);

    useEffect(() => {
        (async () => {
            const [s, r, p, pl] = await Promise.all([
                safeGet("/me/stats"),
                safeGet("/me/reports"),
                safeGet("/me/properties"),
                safeGet("/crm/private-listings", { sort: "recent" }),
            ]);
            setStats(s);
            setReports(r?.items || []);
            setProperties(p?.items || []);
            setPrivates(pl?.items || []);
        })();
        loadSocial();
    }, []);

    const { isDark } = useTheme();
    const chart = isDark
        ? { grid: "#2a3350", tick: "#98a2b8", line: "#5c9dff", surface: "#141a2b", text: "#eef1f8" }
        : { grid: "#e8eef6", tick: "#94a3b8", line: "#2563eb", surface: "#ffffff", text: "#0f172a" };
    const summary = stats?.summary || {};
    const views = summary.total_views || 0;
    const contacts = summary.total_contacts || 0;
    const convRate = views ? ((contacts / views) * 100).toFixed(1) : 0;
    const newPrivates = (privates || []).filter((it) => it.status === "new");

    return (
        <section data-testid="agency-pro-grid" className="pro-dash mb-16 rounded-[28px] bg-[var(--pd-shell)] p-4 md:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">

                <ProCard
                    testid="pro-card-acquisizione"
                    icon={UserSearch}
                    title="Acquisizione privati"
                    subtitle="Trova proprietari privati che vogliono vendere in autonomia."
                    onOpen={() => onOpenSection("acquisizione")}
                    action={<ProAction testid="pro-acquisizione-link" variant="ghost" onClick={() => onOpenSection("acquisizione")}>Apri Acquisizione Privati <ChevronRight size={13} /></ProAction>}
                >
                    <PrivateList items={(privates || []).slice(0, 3)} loading={privates === null} badge="Privato" tone="amber" prefix="acq" />
                </ProCard>

                <ProCard
                    testid="pro-card-nuovi-privati"
                    icon={Megaphone}
                    title="Nuovi privati"
                    subtitle="Annunci pubblicati da privati sui portali, in tempo reale."
                    badge={<span data-testid="pro-realtime-badge" style={{ background: "var(--pd-tag-red-bg)", color: "var(--pd-tag-red)" }} className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold"><span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" /> Tempo reale</span>}
                    onOpen={() => onOpenSection("nuovi-privati")}
                    action={<ProAction testid="pro-nuovi-privati-link" variant="ghost" onClick={() => onOpenSection("nuovi-privati")}>Apri Nuovi Privati <ChevronRight size={13} /></ProAction>}
                >
                    <PrivateList items={(newPrivates.length ? newPrivates : privates || []).slice(0, 3)} loading={privates === null} badge="Nuovo" tone="blue" showTime prefix="new" />
                </ProCard>

                <NextSellerCard onOpenSection={onOpenSection} />

                <FeedCard onOpenSection={onOpenSection} />

                <SocialCard data={social} onOpenSection={onOpenSection} />

                <ProCard
                    testid="pro-card-performance"
                    icon={BarChart3}
                    title="Performance Statistiche"
                    subtitle="Monitora le tue performance in tempo reale."
                    onOpen={() => onOpenSection("statistiche")}
                    action={<ProAction testid="pro-open-statistiche" variant="ghost" onClick={() => onOpenSection("statistiche")}>Vedi statistiche complete <ChevronRight size={13} /></ProAction>}
                >
                    <div className="grid grid-cols-4 gap-2">
                        <ProStat testid="pro-stat-listings" label="Annunci" value={summary.total_listings || 0} />
                        <ProStat testid="pro-stat-views" label="Visual." value={views} />
                        <ProStat testid="pro-stat-contacts" label="Contatti" value={contacts} />
                        <ProStat testid="pro-stat-rate" label="Conv. rate" value={`${convRate}%`} />
                    </div>
                </ProCard>

                <ProCard
                    testid="pro-card-report"
                    icon={FileText}
                    title="I miei report"
                    subtitle="Report mensili con lead, match, clienti e immobili."
                    onOpen={() => onOpenSection("report")}
                    action={<ProAction testid="pro-open-report" variant="ghost" onClick={() => onOpenSection("report")}>Tutti i report <ChevronRight size={13} /></ProAction>}
                >
                    {reports.length === 0 ? (
                        <ProEmpty testid="pro-report-empty">Nessun report disponibile. Il primo arriverà il 1° del mese.</ProEmpty>
                    ) : (
                        <ul className="space-y-1.5">
                            {reports.slice(0, 4).map((r) => (
                                <li key={r.id} data-testid={`pro-report-${r.period}`} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[var(--pd-surface-2)]">
                                    <FileText size={14} className="shrink-0 text-[var(--pd-icon)]" />
                                    <span className="flex-1 truncate text-xs font-medium text-[var(--pd-text)]">{r.period_label}</span>
                                    <span className="text-[10px] text-[var(--pd-muted)]">{new Date(r.generated_at).toLocaleDateString("it-IT")}</span>
                                    <button
                                        data-testid={`pro-report-download-${r.period}`}
                                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/me/reports/${r.id}/pdf`, "_blank")}
                                        className="rounded-lg p-1.5 text-[var(--pd-icon)] hover:bg-[var(--pd-accent-bg)]"
                                        aria-label={`Scarica report ${r.period_label}`}
                                    >
                                        <Download size={13} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </ProCard>

                <ProCard
                    testid="pro-card-annunci"
                    icon={Home}
                    title="Annunci"
                    subtitle="Gestisci e pubblica i tuoi immobili su tutti i portali."
                    onOpen={() => onOpenSection("annunci")}
                    action={<ProAction testid="pro-open-annunci" variant="ghost" onClick={() => onOpenSection("annunci")}>Gestisci annunci <ChevronRight size={13} /></ProAction>}
                >
                    {properties.length === 0 ? (
                        <ProEmpty testid="pro-annunci-empty">Nessun annuncio pubblicato.</ProEmpty>
                    ) : (
                        <div data-testid="pro-last-listing" className="overflow-hidden rounded-xl bg-[var(--pd-surface-2)]">
                            {properties[0]?.photos?.[0] && <img src={properties[0].photos[0]} alt="" className="h-28 w-full object-cover" />}
                            <div className="p-3">
                                <div className="flex items-center gap-2">
                                    <ProTag tone="green">Pubblicato</ProTag>
                                    <span className="truncate text-xs font-semibold text-[var(--pd-text)]">{properties[0].title}</span>
                                </div>
                                <div className="mt-1 text-[11px] text-[var(--pd-muted)]">{properties[0].city} · {properties.length} annunci attivi</div>
                            </div>
                        </div>
                    )}
                </ProCard>

                <ContactsCard onOpenSection={onOpenSection} />

                <ProCard
                    testid="pro-card-visualizzazioni"
                    icon={Eye}
                    title="Visualizzazioni"
                    subtitle="Analizza le visite ai tuoi annunci."
                    onOpen={() => onOpenSection("visualizzazioni")}
                    action={<ProAction testid="pro-open-views" variant="ghost" onClick={() => onOpenSection("visualizzazioni")}>Dettaglio visite <ChevronRight size={13} /></ProAction>}
                >
                    <div className="h-32 w-full">
                        <ResponsiveContainer>
                            <LineChart data={(stats?.per_listing || []).slice(0, 10).map((p) => ({ name: p.city || p.title?.slice(0, 8), views: p.views }))}>
                                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                                <XAxis dataKey="name" tick={{ fontSize: 9, fill: chart.tick }} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${chart.grid}`, background: chart.surface, color: chart.text, fontSize: 12 }} />
                                <Line type="monotone" dataKey="views" stroke={chart.line} strokeWidth={2} dot={{ r: 2 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </ProCard>

                <ProCard
                    testid="pro-card-conversion"
                    icon={TrendingUp}
                    title="Conv. rate"
                    subtitle="Monitora il tasso di conversione dei tuoi annunci."
                    onOpen={() => onOpenSection("conversione")}
                    action={<ProAction testid="pro-open-conversion" variant="ghost" onClick={() => onOpenSection("conversione")}>Dettaglio conversioni <ChevronRight size={13} /></ProAction>}
                >
                    <div className="flex items-center gap-4">
                        <div className="relative h-24 w-24 shrink-0">
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={[{ v: Number(convRate) || 0 }, { v: Math.max(0, 100 - (Number(convRate) || 0)) }]} dataKey="v" innerRadius={28} outerRadius={40} startAngle={90} endAngle={-270} stroke="none">
                                        <Cell fill={chart.line} /><Cell fill={chart.grid} />
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <span data-testid="pro-conv-value" className="absolute inset-0 grid place-items-center text-sm font-bold text-[var(--pd-icon)]">{convRate}%</span>
                        </div>
                        <ul className="flex-1 space-y-1.5 text-xs">
                            <li className="flex items-center justify-between"><span className="flex items-center gap-2 text-[var(--pd-muted)]"><i className="h-2 w-2 rounded-full bg-amber-400" /> Visualizzazioni</span><b className="text-[var(--pd-text)]">{views}</b></li>
                            <li className="flex items-center justify-between"><span className="flex items-center gap-2 text-[var(--pd-muted)]"><i className="h-2 w-2 rounded-full bg-orange-500" /> Contatti</span><b className="text-[var(--pd-text)]">{contacts}</b></li>
                            <li className="flex items-center justify-between"><span className="flex items-center gap-2 text-[var(--pd-muted)]"><i className="h-2 w-2 rounded-full bg-blue-600" /> Conversioni</span><b className="text-[var(--pd-text)]">{contacts}</b></li>
                        </ul>
                    </div>
                </ProCard>

                <ProCard
                    testid="pro-card-notifiche"
                    icon={Bell}
                    title="Notifiche email"
                    subtitle="Ricevi un'email per ogni nuovo messaggio o contatto."
                    onOpen={() => onOpenSection("notifiche")}
                    action={<ProAction testid="pro-open-notifiche" variant="ghost" onClick={() => onOpenSection("notifiche")}>Gestisci notifiche <ChevronRight size={13} /></ProAction>}
                >
                    <div className="space-y-2">
                        <NotifyToggle
                            testid="pro-toggle-email-chat"
                            label="Notifiche chat"
                            on={user?.email_notify_chat !== false}
                            onClick={() => onToggleEmail("email_notify_chat", user?.email_notify_chat !== false)}
                        />
                        <NotifyToggle
                            testid="pro-toggle-email-listing"
                            label="Aggiornamenti annunci"
                            on={user?.email_notify_listing_update !== false}
                            onClick={() => onToggleEmail("email_notify_listing_update", user?.email_notify_listing_update !== false)}
                        />
                    </div>
                </ProCard>

                <ProCard
                    testid="pro-card-aggiornamenti"
                    icon={History}
                    title="Aggiornamenti annunci"
                    subtitle="Riepilogo sullo stato dei tuoi immobili pubblicati."
                    onOpen={() => onOpenSection("aggiornamenti")}
                    action={<ProAction testid="pro-open-aggiornamenti" variant="ghost" onClick={() => onOpenSection("aggiornamenti")}>Vedi stato annunci <ChevronRight size={13} /></ProAction>}
                >
                    <div className="grid grid-cols-3 gap-2">
                        <ProStat testid="pro-upd-total" label="Totali" value={properties.length} />
                        <ProStat testid="pro-upd-active" label="Attivi" value={properties.filter((p) => p.status !== "paused" && p.status !== "sold").length} />
                        <ProStat testid="pro-upd-views" label="Visual." value={views} />
                    </div>
                </ProCard>

                <RequestsCard onOpenSection={onOpenSection} />

                <PartnerCard onOpenSection={onOpenSection} />

                <ZonesCard onChanged={loadSocial} />

                <ProCard
                    testid="pro-card-nuovo-annuncio"
                    icon={Plus}
                    title="Nuovo annuncio"
                    subtitle="Pubblica un nuovo immobile in pochi clic."
                    onOpen={() => onOpenSection("nuovo-annuncio")}
                >
                    <button
                        type="button"
                        onClick={() => onOpenSection("nuovo-annuncio")}
                        data-testid="pro-new-listing-link"
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--pd-accent-border)] bg-[var(--pd-accent-bg)] px-4 py-6 text-xs font-semibold text-[var(--pd-accent)] hover:opacity-90 transition-colors"
                    >
                        <Plus size={16} /> Aggiungi un nuovo annuncio
                    </button>
                </ProCard>
            </div>
        </section>
    );
}

const PrivateList = ({ items, loading, badge, tone, showTime, prefix }) => {
    if (loading) return <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-[var(--pd-surface-2)]" />)}</div>;
    if (!items.length) return <ProEmpty testid={`pro-${prefix}-empty`}>Nessun annuncio privato disponibile. Attiva il modulo Acquisizione Privati per il monitoraggio dei portali.</ProEmpty>;
    return (
        <ul className="space-y-2">
            {items.map((it) => (
                <li key={it.id} data-testid={`pro-${prefix}-item-${it.id}`} className="flex items-center gap-2.5 rounded-xl p-1.5 hover:bg-[var(--pd-surface-2)]">
                    {it.image
                        ? <img src={it.image} alt="" className="h-11 w-14 shrink-0 rounded-lg object-cover" />
                        : <span className="grid h-11 w-14 shrink-0 place-items-center rounded-lg bg-[var(--pd-icon-bg)] text-[var(--pd-icon)]"><Home size={14} /></span>}
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-semibold text-[var(--pd-text)]">{it.title || it.property_type || "Immobile"}</div>
                        <div className="flex items-center gap-1 truncate text-[11px] text-[var(--pd-muted)]">
                            <MapPin size={10} className="text-[var(--pd-icon)]" /> {it.city || "—"}{showTime && it.first_seen_at ? ` · ${timeAgo(it.first_seen_at)}` : ""}
                        </div>
                    </div>
                    <ProTag tone={tone}>{badge}</ProTag>
                </li>
            ))}
        </ul>
    );
};

const NotifyToggle = ({ label, on, onClick, testid }) => (
    <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? "border-[var(--pd-accent-border)] bg-[var(--pd-accent-bg)]" : "border-[var(--pd-border)] bg-[var(--pd-surface)]"}`}
    >
        {on ? <Bell size={15} className="text-[var(--pd-icon)]" /> : <BellOff size={15} className="text-[var(--pd-muted)]" />}
        <span className="flex-1 text-xs font-medium text-[var(--pd-text)]">{label}</span>
        <span className={`relative h-5 w-9 rounded-full transition-colors ${on ? "bg-blue-600" : "bg-slate-300"}`}>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
        </span>
    </button>
);

function FeedCard({ onOpenSection }) {
    const [feed, setFeed] = useState(null);
    const [hook, setHook] = useState(null);

    useEffect(() => {
        api.get("/agency/feed").then((r) => setFeed(r.data?.id ? r.data : {})).catch(() => setFeed({}));
        api.get("/agency/gestionale-webhook").then((r) => setHook(r.data || {})).catch(() => setHook({}));
    }, []);

    const importOn = !!feed?.id;
    const exportOn = !!hook?.webhook_url;
    const loading = feed === null || hook === null;
    const active = importOn || exportOn;
    const lastOk = hook?.webhook_last_status?.status === "success";
    const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

    return (
        <ProCard
            testid="pro-card-feed"
            icon={Rss}
            title="Centro Sincro Feed"
            subtitle="Import annunci dal gestionale ed export giornaliero verso il gestionale."
            onOpen={() => onOpenSection("feed")}
            badge={loading ? null : (
                <span data-testid="pro-feed-status" style={{ background: active ? "var(--pd-tag-green-bg)" : "var(--pd-tag-slate-bg)", color: active ? "var(--pd-tag-green)" : "var(--pd-tag-slate)" }} className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold">
                    <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"}`} /> {active ? "Attivo" : "Non collegato"}
                </span>
            )}
            action={<ProAction testid="pro-open-feed" variant="solid" onClick={() => onOpenSection("feed")}>Gestisci collegamento <ChevronRight size={13} /></ProAction>}
        >
            <div className="space-y-2">
                <div data-testid="pro-feed-import-row" className="flex items-start gap-2 rounded-xl bg-[var(--pd-surface-2)] px-3 py-2.5">
                    <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${importOn ? "bg-emerald-500" : "bg-slate-400/60"}`} />
                    <div className="min-w-0 text-[11px]">
                        <span className="font-semibold text-[var(--pd-text)]">Import: </span>
                        {importOn
                            ? <span className="text-[var(--pd-ok)]">attivo <span className="block truncate text-[var(--pd-muted)]">{feed.url}</span></span>
                            : <span className="text-[var(--pd-muted)]">non configurato</span>}
                    </div>
                </div>
                <div data-testid="pro-feed-export-row" className="flex items-start gap-2 rounded-xl bg-[var(--pd-surface-2)] px-3 py-2.5">
                    <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${exportOn ? (hook?.webhook_last_status && !lastOk ? "bg-red-500" : "bg-emerald-500") : "bg-slate-400/60"}`} />
                    <div className="min-w-0 text-[11px]">
                        <span className="font-semibold text-[var(--pd-text)]">Export webhook: </span>
                        {exportOn
                            ? <span className={lastOk || !hook?.webhook_last_status ? "text-[var(--pd-ok)]" : "text-[var(--pd-err)]"}>attivo <span className="block text-[var(--pd-muted)]">{hook.webhook_last_sent_at ? `ultimo invio ${fmt(hook.webhook_last_sent_at)} · ${lastOk ? "ok" : `errore${hook.webhook_last_status?.http_status ? ` HTTP ${hook.webhook_last_status.http_status}` : ""}`}` : "in attesa del primo invio (07:00)"}</span></span>
                            : <span className="text-[var(--pd-muted)]">non configurato</span>}
                    </div>
                </div>
            </div>
        </ProCard>
    );
}

function SocialCard({ data, onOpenSection }) {
    const [city, setCity] = useState("");
    const cities = data?.cities || [];
    const items = (data?.items || []).filter((it) => !city || it.city === city);
    const fresh = items.filter((it) => it.is_new).length;

    return (
        <ProCard
            testid="pro-card-social"
            icon={ScanSearch}
            title="Clienti Pronti sui Social"
            subtitle="Potenziali acquirenti e affittuari delle tue zone, trovati su Facebook e Instagram."
            onOpen={() => onOpenSection("social")}
            action={<ProAction testid="pro-social-scan-btn" onClick={() => onOpenSection("social")}><RefreshCw size={13} /> Scansiona ora</ProAction>}
        >
            <select
                data-testid="pro-social-city-filter"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="pd-input mb-3"
            >
                <option value="">Tutte le zone operative</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
                <ProStat testid="pro-social-buyers" label="Potenziali acquirenti" value={items.length} />
                <ProStat testid="pro-social-new" label="Nuovi contatti" value={fresh} />
            </div>
        </ProCard>
    );
}

function ContactsCard({ onOpenSection }) {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get("/contact-requests/received", { params: { status_filter: "pending" } })
            .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    }, []);
    return (
        <ProCard
            testid="pro-card-contatti"
            icon={Users}
            title="Contatti ricevuti"
            subtitle="Tutti i lead e i potenziali clienti in un unico posto."
            onOpen={() => onOpenSection("contatti")}
            action={<ProAction testid="pro-open-contatti" variant="ghost" onClick={() => onOpenSection("contatti")}>Vedi tutti i contatti <ChevronRight size={13} /></ProAction>}
        >
            {!items?.length ? (
                <ProEmpty testid="pro-contatti-empty">Nessun contatto in attesa al momento.</ProEmpty>
            ) : (
                <ul className="space-y-1.5">
                    {items.slice(0, 4).map((r) => (
                        <li key={r.id} data-testid={`pro-contatto-${r.id}`} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-[var(--pd-surface-2)]">
                            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--pd-icon-bg)] text-[var(--pd-icon)]"><Users size={13} /></span>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-xs font-semibold text-[var(--pd-text)]">{r.level === "chat" ? "Richiesta chat" : "Richiesta contatti"}</div>
                                <div className="truncate text-[11px] text-[var(--pd-muted)]">{timeAgo(r.created_at)}</div>
                            </div>
                            <ChevronRight size={14} className="shrink-0 text-[var(--pd-muted)]" />
                        </li>
                    ))}
                </ul>
            )}
        </ProCard>
    );
}

const REQ_TABS = [{ id: "pending", l: "In attesa" }, { id: "accepted", l: "Accettate" }, { id: "declined", l: "Rifiutate" }];

function RequestsCard({ onOpenSection }) {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState(null);
    useEffect(() => {
        setItems(null);
        api.get("/contact-requests/received", { params: { status_filter: tab } })
            .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    }, [tab]);

    return (
        <ProCard
            testid="pro-card-richieste"
            icon={MessageSquare}
            title="Richieste di contatto"
            subtitle="Gestisci le richieste in attesa di consenso."
            onOpen={() => onOpenSection("richieste")}
            action={<ProAction testid="pro-open-richieste" variant="ghost" onClick={() => onOpenSection("richieste")}>Apri richieste <ChevronRight size={13} /></ProAction>}
        >
            <div className="mb-3 flex gap-1.5">
                {REQ_TABS.map((t) => (
                    <button
                        key={t.id}
                        data-testid={`pro-req-tab-${t.id}`}
                        onClick={() => setTab(t.id)}
                        className={`flex-1 rounded-full px-2 py-1.5 text-[11px] font-semibold transition-colors ${tab === t.id ? "bg-blue-600 text-white" : "bg-[var(--pd-surface-2)] text-[var(--pd-muted)] hover:text-[var(--pd-text)]"}`}
                    >
                        {t.l}
                    </button>
                ))}
            </div>
            <div data-testid="pro-req-count" className="text-xs text-[var(--pd-muted)]">
                {items === null ? "Caricamento…" : items.length === 0 ? "Nessuna richiesta in questa categoria." : `${items.length} richiest${items.length === 1 ? "a" : "e"} · ultima ${timeAgo(items[0].created_at)}`}
            </div>
        </ProCard>
    );
}

function PartnerCard({ onOpenSection }) {
    const [status, setStatus] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = () => api.get("/agency/partnership/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
    useEffect(() => { load(); }, []);

    const redeem = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setBusy(true); setMsg(null);
        try {
            const { data } = await api.post("/agency/partnership/redeem", { code: code.trim() });
            setMsg({ ok: true, text: data.message || "Codice attivato" });
            setCode("");
            await load();
        } catch (err) {
            setMsg({ ok: false, text: err?.response?.data?.detail || "Impossibile attivare il codice" });
        } finally { setBusy(false); }
    };

    return (
        <ProCard
            testid="pro-card-partner"
            icon={Handshake}
            title="Codice Partner"
            subtitle="Sblocca la pubblicazione illimitata attivando il codice del tuo gruppo."
            badge={<ProTag tone={status?.is_partner ? "green" : "slate"}>{status?.is_partner ? "PARTNER" : "STANDARD"}</ProTag>}
            onOpen={() => onOpenSection("partner")}
            action={<ProAction testid="pro-open-partner" variant="ghost" onClick={() => onOpenSection("partner")}>Dettagli partnership <ChevronRight size={13} /></ProAction>}
        >
            <form onSubmit={redeem} className="space-y-2">
                <input
                    data-testid="pro-partner-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Inserisci Codice Partner (es. GRUPPO-REMAX)"
                    className="pd-input"
                />
                <button data-testid="pro-partner-activate-btn" type="submit" disabled={busy || !code.trim()} className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                    {busy ? "Attivazione…" : "Attiva Codice"}
                </button>
            </form>
            {msg && <div data-testid="pro-partner-msg" className={`mt-2 text-[11px] ${msg.ok ? "text-[var(--pd-ok)]" : "text-[var(--pd-err)]"}`}>{msg.text}</div>}
        </ProCard>
    );
}

function ZonesCard({ onChanged }) {
    const [zones, setZones] = useState(null);
    const [value, setValue] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => { api.get("/profile/zone-operative").then((r) => setZones(r.data.zone_operative || [])).catch(() => setZones([])); }, []);

    const save = async (next) => {
        setBusy(true); setMsg(null);
        try {
            const { data } = await api.put("/profile/zone-operative", { zone_operative: next });
            setZones(data.zone_operative || []);
            onChanged?.();
        } catch (err) {
            setMsg(err?.response?.data?.detail || "Impossibile salvare le zone");
        } finally { setBusy(false); }
    };
    const add = (e) => {
        e.preventDefault();
        const v = value.trim();
        if (!v) return;
        if ((zones || []).some((z) => z.toLowerCase() === v.toLowerCase())) { setValue(""); return; }
        setValue("");
        save([...(zones || []), v]);
    };

    return (
        <ProCard
            testid="pro-card-zone"
            icon={Map}
            title="Zone Operative"
            subtitle="Le città in cui lavori: usate automaticamente da Clienti Pronti sui Social, ricerche e radar."
            badge={<ProTag tone="blue" testid="pro-zone-count">{zones ? `${zones.length} zon${zones.length === 1 ? "a" : "e"}` : "…"}</ProTag>}
        >
            <form onSubmit={add} className="flex gap-2">
                <input
                    data-testid="pro-zone-input"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Aggiungi città (es. Milano)"
                    className="pd-input"
                />
                <button data-testid="pro-zone-add-btn" type="submit" disabled={busy || !value.trim()} className="shrink-0 rounded-xl bg-blue-600 px-3 text-white hover:bg-blue-700 disabled:opacity-50" aria-label="Aggiungi zona">
                    <Plus size={15} />
                </button>
            </form>
            <div className="mt-3 flex flex-wrap gap-1.5">
                {zones === null ? (
                    <span className="text-[11px] text-[var(--pd-muted)]">Caricamento…</span>
                ) : zones.length === 0 ? (
                    <ProEmpty testid="pro-zone-empty">Nessuna zona operativa. Aggiungi la prima città per attivare le funzioni geografiche.</ProEmpty>
                ) : zones.map((z) => (
                    <span key={z} data-testid={`pro-zone-chip-${z}`} className="inline-flex items-center gap-1 rounded-full bg-[var(--pd-surface-2)] py-1 pl-2.5 pr-1 text-[11px] font-medium text-[var(--pd-text)]">
                        <MapPin size={10} className="text-[var(--pd-icon)]" /> {z}
                        <button type="button" data-testid={`pro-zone-remove-${z}`} disabled={busy} onClick={() => save(zones.filter((x) => x !== z))} className="grid h-5 w-5 place-items-center rounded-full text-[var(--pd-muted)] hover:bg-[var(--pd-tag-red-bg)] hover:text-[var(--pd-tag-red)]" aria-label={`Rimuovi ${z}`}>
                            <X size={11} />
                        </button>
                    </span>
                ))}
            </div>
            {msg && <div data-testid="pro-zone-msg" className="mt-2 text-[11px] text-[var(--pd-err)]">{msg}</div>}
        </ProCard>
    );
}

function NextSellerCard({ onOpenSection }) {
    const [leads, setLeads] = useState(null);
    const [mod, setMod] = useState(null);

    useEffect(() => {
        api.get("/agency/addons/status").then((r) => setMod(r.data?.modules?.next_seller || null)).catch(() => setMod(null));
        api.get("/crm/next-seller", { params: { limit: 3 } }).then((r) => setLeads(r.data?.items || [])).catch(() => setLeads([]));
    }, []);

    const daysLeft = mod?.trial_ends_at ? Math.max(0, Math.ceil((new Date(mod.trial_ends_at) - Date.now()) / 86400000)) : null;
    const badge = !mod ? null : mod.reason === "trial"
        ? <ProTag tone="blue" testid="pro-next-seller-status">In prova · {daysLeft}g</ProTag>
        : mod.unlocked
            ? <ProTag tone="green" testid="pro-next-seller-status">Attivo</ProTag>
            : <ProTag tone="slate" testid="pro-next-seller-status">Non attivo</ProTag>;

    return (
        <ProCard
            testid="pro-card-next-seller"
            icon={Target}
            title="Next Seller"
            subtitle="Venditori in procinto di mettere casa sul mercato, prima degli altri."
            badge={badge}
            onOpen={() => onOpenSection("next-seller")}
            action={<ProAction testid="pro-next-seller-link" variant="ghost" onClick={() => onOpenSection("next-seller")}>Apri Next Seller <ChevronRight size={13} /></ProAction>}
        >
            {leads === null ? (
                <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-[var(--pd-surface-2)]" />)}</div>
            ) : leads.length === 0 ? (
                <ProEmpty testid="pro-next-seller-empty">{mod && !mod.unlocked ? "Attiva il modulo Next Seller per vedere i venditori in arrivo nelle tue zone." : "Nessun segnale rilevato nelle tue zone nelle ultime notti."}</ProEmpty>
            ) : (
                <ul className="space-y-2">
                    {leads.map((l) => (
                        <li key={l.id} data-testid={`pro-next-seller-item-${l.id}`} className="flex items-center gap-2.5 rounded-xl p-1.5 hover:bg-[var(--pd-surface-2)]">
                            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-[var(--pd-icon-bg)] text-sm font-bold text-[var(--pd-icon)]">{Math.round(l.score || 0)}</span>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-xs font-semibold text-[var(--pd-text)]">{l.tipologia || "Immobile"} · {l.zona || l.citta}</div>
                                <div className="truncate text-[11px] text-[var(--pd-muted)]">{l.segnale_principale || l.source_type}</div>
                            </div>
                            <ProTag tone={(l.score || 0) >= 80 ? "green" : "amber"}>{(l.score || 0) >= 80 ? "Caldo" : "Segnale"}</ProTag>
                        </li>
                    ))}
                </ul>
            )}
        </ProCard>
    );
}
